<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import {
  getBranding,
  updateBranding,
  deleteBrandingAsset,
} from 'dashboard/unichat/api/unichat.js';

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

const loading = ref(false);
const saving = ref(false);

const ASSETS = [
  {
    key: 'brand_logo',
    label: 'Logo principal',
    description: 'Usada na tela de login e na barra lateral do sistema.',
  },
  {
    key: 'brand_logo_internal',
    label: 'Logo interna (opcional)',
    description: 'Substitui a logo principal dentro do sistema, se enviada.',
  },
  {
    key: 'brand_favicon',
    label: 'Favicon',
    description: 'Ícone exibido na aba do navegador.',
  },
];

const currentUrls = ref({
  brand_logo: '',
  brand_logo_internal: '',
  brand_favicon: '',
});

const pendingFiles = ref({
  brand_logo: null,
  brand_logo_internal: null,
  brand_favicon: null,
});

const previewUrls = ref({
  brand_logo: '',
  brand_logo_internal: '',
  brand_favicon: '',
});

const fetchBranding = async () => {
  loading.value = true;
  try {
    const { data } = await getBranding(accountId.value);
    currentUrls.value = {
      brand_logo: data.logo_url || '',
      brand_logo_internal: data.logo_internal_url || '',
      brand_favicon: data.favicon_url || '',
    };
  } catch {
    useAlert('Erro ao carregar configurações de marca.');
  } finally {
    loading.value = false;
  }
};

onMounted(fetchBranding);

const onFileChange = (key, event) => {
  const file = event.target.files?.[0];
  if (!file) return;
  pendingFiles.value[key] = file;
  previewUrls.value[key] = URL.createObjectURL(file);
};

const displayUrl = key => previewUrls.value[key] || currentUrls.value[key];

const hasPendingChanges = computed(() =>
  Object.values(pendingFiles.value).some(f => f !== null)
);

const save = async () => {
  saving.value = true;
  try {
    const formData = new FormData();
    ASSETS.forEach(({ key }) => {
      if (pendingFiles.value[key]) {
        formData.append(key, pendingFiles.value[key]);
      }
    });
    const { data } = await updateBranding(accountId.value, formData);
    currentUrls.value = {
      brand_logo: data.logo_url || '',
      brand_logo_internal: data.logo_internal_url || '',
      brand_favicon: data.favicon_url || '',
    };
    pendingFiles.value = { brand_logo: null, brand_logo_internal: null, brand_favicon: null };
    previewUrls.value = { brand_logo: '', brand_logo_internal: '', brand_favicon: '' };
    useAlert('Aparência da marca atualizada com sucesso!');
  } catch {
    useAlert('Erro ao salvar. Verifique o formato/tamanho dos arquivos (máx. 5MB).');
  } finally {
    saving.value = false;
  }
};

const removeAsset = async key => {
  if (!confirm('Remover esse arquivo e voltar ao padrão?')) return;
  try {
    const { data } = await deleteBrandingAsset(accountId.value, key);
    currentUrls.value = {
      brand_logo: data.logo_url || '',
      brand_logo_internal: data.logo_internal_url || '',
      brand_favicon: data.favicon_url || '',
    };
    useAlert('Arquivo removido.');
  } catch {
    useAlert('Erro ao remover arquivo.');
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    loading-message="Carregando aparência da marca..."
  >
    <template #header>
      <BaseSettingsHeader
        title="Aparência da Marca"
        description="Troque a logo, a logo interna e o favicon usados em todo o sistema."
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 max-w-2xl py-4">
        <div
          v-for="asset in ASSETS"
          :key="asset.key"
          class="flex items-center gap-4 p-4 border border-n-weak rounded-xl"
        >
          <div
            class="flex items-center justify-center shrink-0 rounded-lg overflow-hidden"
            style="width:64px;height:64px;background:rgba(34,197,94,.06);border:1px solid rgba(34,197,94,.18);"
          >
            <img
              v-if="displayUrl(asset.key)"
              :src="displayUrl(asset.key)"
              alt=""
              class="max-w-full max-h-full object-contain"
            />
            <i v-else class="i-lucide-image size-6 text-n-slate-9" />
          </div>

          <div class="flex-1 min-w-0">
            <div class="text-heading-3 text-n-slate-12">{{ asset.label }}</div>
            <div class="text-body-main text-n-slate-11">{{ asset.description }}</div>
          </div>

          <div class="flex items-center gap-2 shrink-0">
            <label class="cursor-pointer">
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/x-icon"
                class="hidden"
                @change="e => onFileChange(asset.key, e)"
              />
              <span
                class="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium"
                style="background:rgba(34,197,94,.10);color:#22C55E;border:1px solid rgba(34,197,94,.25);"
              >
                Escolher arquivo
              </span>
            </label>
            <Button
              v-if="currentUrls[asset.key]"
              v-tooltip.top="'Remover'"
              icon="i-lucide-trash-2"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="removeAsset(asset.key)"
            />
          </div>
        </div>

        <div class="flex justify-end pt-2">
          <Button
            label="Salvar alterações"
            :is-loading="saving"
            :disabled="!hasPendingChanges"
            @click="save"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
